import { TextField } from "@/app/fields";
import type { Channel, Supplier } from "@/app/master";
import { nameOptions } from "@/app/queries";
import type { Errors } from "@/app/useDraft";
import { Field, Form, Combobox } from "@/ui";
import { PriceFields } from "./PriceFields";
import { lockedChargeOn, TELEGRAM_CHARGE_ON, type CostDraft, type RateDraft } from "./costDraft";

interface CostProps { draft: CostDraft; errors: Errors<CostDraft>; set: <K extends keyof CostDraft>(k: K, v: CostDraft[K]) => void; channels?: Channel[]; suppliers?: Supplier[] }

/** 成本价表单（docs/03 3.8）：币种按这条价格填，同一个供应商、同一条通道可以用不同币种（决策 D85）。选了 Telegram 通道时计费类型固定为按送达（D207 第 6 条）。 */
export function CostPriceForm({ draft, errors, set, channels, suppliers }: CostProps) {
  const locked = lockedChargeOn(channels?.find((c) => String(c.id) === draft.channel_id));
  const pickChannel = (v: string) => {
    set("channel_id", v);
    if (lockedChargeOn(channels?.find((c) => String(c.id) === v))) set("charge_on", "delivered");
  };
  return (
    <Form columns={2}>
      <Field label="供应商" required error={errors.supplier_id}>
        <Combobox block searchPlaceholder="搜索供应商名称或 ID" options={nameOptions(suppliers?.filter((s) => !s.system))} value={draft.supplier_id || null} onChange={(v) => { set("supplier_id", v); set("channel_id", ""); }} placeholder="选择供应商" invalid={!!errors.supplier_id} />
      </Field>
      <Field label="通道" required error={errors.channel_id}>
        <Combobox block searchPlaceholder="搜索通道名称或 ID" options={nameOptions(channels?.filter((c) => String(c.supplier_id) === draft.supplier_id))} value={draft.channel_id || null} onChange={pickChannel} disabled={!draft.supplier_id} placeholder="选择通道" invalid={!!errors.channel_id} />
      </Field>
      <PriceFields idPrefix="c" draft={draft} errors={errors} set={set} chargeOnLock={locked && TELEGRAM_CHARGE_ON} />
    </Form>
  );
}

interface RateProps { draft: RateDraft; errors: Errors<RateDraft>; set: <K extends keyof RateDraft>(k: K, v: RateDraft[K]) => void }

/** 汇率表单（docs/03 3.7）：1 原币等于多少基准币。 */
export function RateForm({ draft, errors, set }: RateProps) {
  return (
    <Form columns={2}>
      <TextField id="x-currency" label="币种" required mono value={draft.currency} error={errors.currency} onChange={(v) => set("currency", v.toUpperCase())} />
      <TextField id="x-rate" label="汇率" required mono value={draft.rate} error={errors.rate} hint="1 原币等于多少基准币；基准币自身填 1" onChange={(v) => set("rate", v)} />
      <TextField id="x-from" label="生效时间" required type="datetime-local" mono value={draft.effective_from} error={errors.effective_from} hint="北京时间" onChange={(v) => set("effective_from", v)} />
    </Form>
  );
}
